import { createAuthHeaders } from "api/headers";
import Sentry from "lib/sentry/init";

export interface ErrorContent {
  status: number;
  content: string;
  AutenticationStatusCode?: number;
}

type RequestConfig = {
  withAuth: boolean;
  headers: { key: string; value: string }[];
};

const defaultConfig: RequestConfig = { withAuth: true, headers: [] };

export const isLocalhost = Boolean(
  window.location.hostname === "localhost" ||
    // [::1] is the IPv6 localhost address.
    window.location.hostname === "[::1]" ||
    // 127.0.0.1/8 is considered localhost for IPv4.
    window.location.hostname.match(
      /^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/
    )
);

export const request =
  (method: string, includeAuth = true) =>
  async (
    endpoint: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    payload?: any,
    config: RequestConfig = defaultConfig
  ) => {
    const { withAuth } = { ...defaultConfig, ...config };
    const headers =
      withAuth && includeAuth
        ? await createAuthHeaders(endpoint)
        : new Headers();

    if (headers) {
      config.headers.forEach(header =>
        headers.append(header.key, header.value)
      );
    }

    if (!isLocalhost) {
      headers.append("X-Requested-With", "XMLHttpRequest");
    }

    if (!(payload instanceof FormData)) {
      headers.append("Content-Type", "application/json");
    }

    const options: RequestInit = {
      headers,
      method
    };

    if (payload) {
      options.body =
        payload instanceof FormData || payload instanceof File
          ? payload
          : JSON.stringify(payload);
    }

    const getPromiseForStatus = (response: Response, stream: Promise<any>) =>
      stream.then(content =>
        response.status >= 200 && response.status < 300
          ? content
          : Promise.reject({
              status: response.status,
              content
            })
      );

    try {
      const response = await window.fetch(endpoint, options);

      if (
        response.status === 204 ||
        parseInt(response.headers.get("Content-Length") as string) === 0
      ) {
        return response.status >= 200 && response.status < 300
          ? Promise.resolve("")
          : Promise.reject(response);
      }

      const contentType = response.headers.get("Content-Type");
      const isJsonResponse =
        contentType && contentType.indexOf("application/json") !== -1;
      return await getPromiseForStatus(
        response,
        isJsonResponse ? response.json() : response.text()
      );
    } catch (err) {
      console.error(err);
      Sentry.captureException(err, {
        extra: {
          endpoint
        }
      });
      throw err;
    }
  };

export const get = request("GET");
export const post = request("POST");
export const put = request("PUT");
export const del = request("DELETE");
export const patch = request("PATCH");
